// 影像牆格子的自動重連（plan/vms-web-v0.md LIVE-04，Eddy 2026-09-27）：
//   - 可恢復的中斷（網路、信令斷線、設備離線 / 重連、通道離線、設備影像引擎錯誤、ICE 失敗、觀看人數已滿（設備 MAX_SESSIONS_REACHED、
//     平台 VIEWER_LIMIT：信令 error code 或 REST 429）、影像停住）→ 指數退避 2 → 4 → 8 → … 秒，上限 60 秒，無限次
//     （設備 start_viewing 回的 status 由 media hub 原樣轉給信令 response 與 façade session failure_code，vms-system §5.1；
//      nvrd 142bef5 起：槽滿 MAX_SESSIONS_REACHED、該路沒有可推的視訊 CHANNEL_OFFLINE、引擎建不起來 HARDWARE_ERROR）
//   - 權限 / 席次 / 授權類（無權限、權益 / 席次 / 額度、session 被收回或被接管、要重新驗證、瀏覽器不支援）→ 不重試，維持失敗畫面
//   - 重試也不會變的設備拒絕（vms-system §5.1，2026-09-28）：`CODEC_UNSUPPORTED`（設備只有瀏覽器解不了的編碼、也沒有可用的 H.264 子碼流；
//     文案請使用者把這個通道的子碼流改成 H.264）、`INVALID_PAYLOAD`（設備認為請求本身格式不符）→ 不重試，維持失敗畫面
//   - 收到第一個影格就把次數歸零

import type { LiveStopReason } from '@kinio/web-sdk';
import { errorKeys } from '../../lib/errorKeys';

export const RECONNECT_BASE_MS = 2_000;
export const RECONNECT_MAX_MS = 60_000;
/** 已經在播、影像停住多久算中斷（currentTime 不前進；觸發重連）。 */
export const STALL_MS = 15_000;
/**
 * 一次連線（含每次自動重連）開始後多久還沒有第一個影格、也沒有任何錯誤，就當成這次失敗、排下一次重連（CONNECT_TIMEOUT）。
 * SDK 的每一步不一定都有逾時：façade 預留是一般 fetch、ICE 沒拿到遠端 candidate 時可能一直停在 checking（W17 第二輪 2026-09-27：
 * 信令一再被 hub 關掉、dev 部署期間，8 格停在「重新連線中（第 1 次）」15 分鐘沒錯誤也沒再重連）。
 * 要比 start_viewing 的指令逾時（SDK COMMAND_TIMEOUT_MS 30 秒）加 ICE 長，也給 HaLow / 電池攝影機醒來的時間。
 */
export const CONNECT_TIMEOUT_MS = 45_000;

/** 第 attempt 次重連前等多久（attempt 從 1 起）：2、4、8、16、32、60、60… 秒。 */
export function backoffMs(attempt: number): number {
  const n = Math.max(1, Math.floor(attempt));
  return Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * 2 ** Math.min(n - 1, 16));
}

// 錯誤碼是 UPPER_SNAKE（SPEC #eng-10：平台 JSON 的 error、媒體信令的 code、SDK 自己的碼）；façade session 的
// failure_code 是結束原因（lower_snake，rename-map keep_lower）——兩種都會走到這裡，比對前一律轉大寫。
const TERMINAL_CODES = new Set([
  'FORBIDDEN',
  'UNAUTHORIZED',
  'NO_SESSION',
  'NOT_FOUND',
  'ACCESS_REVOKED',
  'SESSION_REVOKED',
  'SESSION_ENDED', // 被別人（owner / admin）用 API 結束 = 被收回或接管
  'SESSION_TAKEN',
  'SESSION_PREEMPTED',
  'PREEMPTED',
  'TAKEN_OVER',
  'REAUTH_REQUIRED',
  'NOT_ENTITLED',
  'NO_WEBRTC',
  // 設備拒絕、而且同一個請求重送結果也一樣（vms-system §5.1；223 ch2 H.265 在不支援 H.265 的瀏覽器曾一直重連，QA 2026-09-28）
  'CODEC_UNSUPPORTED',
  'UNSUPPORTED_CODEC',
  'INVALID_PAYLOAD',
  // LIVE-12 中繼節流（relayGuard.ts）：閒置 / 單次到期，要使用者按「繼續觀看」
  'RELAY_IDLE',
  'RELAY_LIMIT',
]);
const TERMINAL_PREFIXES = ['ENTITLEMENT', 'SEAT', 'QUOTA', 'NOT_ENTITLED'];

/**
 * 這個錯誤要不要自動重連。status：HTTP 狀態（KinioError.status；信令錯誤是 409、網路是 0）。
 * 401 / 402 / 403 / 404 一律不重試（授權類）；其餘看錯誤碼。
 */
export function isRetryable(code: string | null | undefined, status = 0): boolean {
  if (status === 401 || status === 402 || status === 403 || status === 404) return false;
  const c = (code ?? '').toUpperCase();
  if (!c) return true;
  if (TERMINAL_CODES.has(c)) return false;
  return !TERMINAL_PREFIXES.some((p) => c.startsWith(p));
}

/** 格子文案的 i18n key（live.err_*；大小寫都對得上，見 lib/errorKeys）。 */
export function liveErrorKeys(code: string): string[] {
  return errorKeys('live', code);
}

/**
 * 重連（自動或按〔重新連線〕）時舊連線的停止原因（stop_viewing.reason，plan SPEC #media-conn-stats「停止原因」；app/vms#24）：
 * 等不到第一個影格（`CONNECT_TIMEOUT`、或還在「回應較慢」那段就按了重試）= connect_timeout，其他 = retry——兩個平台都算連不上，
 * 不會被當成觀看端自己放棄。
 */
export function retryStopReason(err: string | null, slow: boolean): LiveStopReason {
  return err === 'CONNECT_TIMEOUT' || (!err && slow) ? 'connect_timeout' : 'retry';
}

/** 這格收掉（不是重連）的停止原因：換了碼流 = stream_switch，其他 = 格子自己的離開原因（影像牆 multiview_leave、其他 user_leave）。 */
export function leaveStopReason(was: 'main' | 'sub' | undefined, now: 'main' | 'sub' | undefined, leave: LiveStopReason): LiveStopReason {
  return was !== now ? 'stream_switch' : leave;
}
